/**
 * Human decision queue. Routine running/submitted work intentionally stays on
 * the Board; this surface contains only decisions that require a person.
 */

import { artifactLink, bindArtifactLightboxes, imagePreview } from "./artifact-links.js";

const DECISION_STATUSES = new Set(["review", "escalated", "stale"]);

function readableMeasure(value) {
  return Number.isFinite(Number(value))
    ? `${Number(value).toLocaleString("en-US", { maximumFractionDigits: 3 })} m`
    : "Not recorded";
}

/** Present immutable review evidence in language a human can decide from. */
export function artifactReviewSummary(review) {
  const rawRequest = typeof review?.changeNote === "string" ? review.changeNote.trim() : "";
  const requestedChange = rawRequest.includes("Requested change:")
    ? rawRequest.slice(rawRequest.lastIndexOf("Requested change:") + "Requested change:".length).trim()
    : rawRequest;
  const structure = Array.isArray(review?.body?.radialStructures) ? review.body.radialStructures[0] : null;
  if (!structure) return { structure: null, request: requestedChange, facts: [] };
  const segment = structure.segment || structure;
  const opening = structure.longitudinalOpening || null;
  const road = opening?.roadLayout || null;
  const facts = [
    { label: "Deliverable", value: "1 reusable segment" },
    { label: "Segment", value: `${readableMeasure(segment.centrelineLengthM)} long · ${readableMeasure(segment.radialDepthM)} deep` },
    opening
      ? { label: "Tunnel", value: `${readableMeasure(opening.clearRadialWidthM)} wide × ${readableMeasure(opening.clearHeightM)} high` }
      : { label: "Opening", value: "Solid segment" },
    opening
      ? { label: "Shell", value: `${readableMeasure(opening.floorThicknessM)} floor · ${readableMeasure(opening.leftWallThicknessM)} / ${readableMeasure(opening.rightWallThicknessM)} sides` }
      : null,
    road
      ? { label: "Road intent", value: `${road.laneCount} lanes · ${readableMeasure(road.laneWidthM)} each` }
      : null,
  ].filter(Boolean);
  return { structure, opening, road, request: requestedChange, facts };
}

export function initReviews({
  api,
  toast,
  esc,
  fileUrl = (key) => `/files/${key}`,
  openTicket = null,
  openWorld = null,
  openAssetReview = null,
  onCountChange = null,
  hrefFor = null,
  onNavigate = null,
  onRouteStateChange = null,
}) {
  const root = document.getElementById("panel");
  let tickets = [];
  let artifactReviews = [];
  let selectedId = "";
  let context = null;
  let artifacts = [];
  let loadingContext = false;
  let returnFocus = null;
  let currentFilter = ["all", "review", "escalated", "stale"].includes(localStorage.getItem("wl_review_filter"))
    ? localStorage.getItem("wl_review_filter")
    : "all";

  async function render() {
    root.innerHTML = `<main class="needs-page" aria-busy="true"><div class="production-loading">Loading decisions…</div></main>`;
    try {
      const projectId = document.body.dataset.projectId || "";
      if (!projectId) {
        tickets = [];
        onCountChange?.(0);
        root.innerHTML = `<div class="needs-page production-empty"><h2>Select a project</h2><p>Reviews are isolated per project.</p></div>`;
        return;
      }
      const [response, artifactResponse] = await Promise.all([
        api(`/tickets?projectId=${encodeURIComponent(projectId)}`),
        api(`/projects/${encodeURIComponent(projectId)}/reviews`),
      ]);
      tickets = (Array.isArray(response.tickets) ? response.tickets : [])
        .filter((ticket) => DECISION_STATUSES.has(ticket.status))
        .sort(decisionSort);
      artifactReviews = Array.isArray(artifactResponse.reviews) ? artifactResponse.reviews : [];
      onCountChange?.(tickets.length + artifactReviews.length);
      if (selectedId && !tickets.some((ticket) => ticket.id === selectedId) && !artifactReviews.some((review) => review.id === selectedId)) {
        selectedId = "";
        context = null;
        artifacts = [];
      }
      draw();
    } catch (error) {
      root.innerHTML = `<div class="needs-page production-empty"><h2>Reviews unavailable</h2><p>${esc(readableError(error))}</p><button type="button" data-review-retry>Try again</button></div>`;
      root.querySelector("[data-review-retry]")?.addEventListener("click", render);
    }
  }

  async function refreshCount() {
    try {
      const projectId = document.body.dataset.projectId || "";
      if (!projectId) return 0;
      const [response, artifactResponse] = await Promise.all([
        api(`/tickets?projectId=${encodeURIComponent(projectId)}`),
        api(`/projects/${encodeURIComponent(projectId)}/reviews`),
      ]);
      const count = (Array.isArray(response.tickets) ? response.tickets : [])
        .filter((ticket) => DECISION_STATUSES.has(ticket.status)).length;
      const total = count + (Array.isArray(artifactResponse.reviews) ? artifactResponse.reviews.length : 0);
      onCountChange?.(total);
      return total;
    } catch {
      return null;
    }
  }

  function draw() {
    const visible = currentFilter === "all"
      ? tickets
      : tickets.filter((ticket) => ticket.status === currentFilter);
    const visibleArtifacts = currentFilter === "all"
      ? artifactReviews
      : artifactReviews.filter((review) => review.status === currentFilter);
    const total = tickets.length + artifactReviews.length;

    root.innerHTML = `<main class="needs-page">
      <header class="production-head needs-head">
        <div>
          <p class="production-eyebrow">Human gates</p>
          <h2>Needs you <span>${total}</span></h2>
          <p>Review outcomes and resolve exceptions. Agent activity stays on the Board.</p>
        </div>
        <button type="button" data-review-refresh>Refresh</button>
      </header>

      <nav class="needs-filters" aria-label="Decision filters">
        ${filterButton("all", "All", total)}
        ${filterButton("review", "Review", countStatus("review"))}
        ${filterButton("escalated", "Escalated", countStatus("escalated"))}
        ${filterButton("stale", "Out of date", countStatus("stale"))}
      </nav>

      <section class="needs-list" aria-label="Decisions">
        ${visibleArtifacts.map(artifactDecisionCard).join("")}${visible.length ? visible.map(decisionCard).join("") : !visibleArtifacts.length ? emptyState() : ""}
      </section>

      <div class="review-scrim ${selectedId ? "open" : ""}" data-review-close aria-hidden="true"></div>
      <aside class="review-drawer ${selectedId ? "open" : ""}" role="dialog" aria-modal="true" aria-labelledby="review-dialog-title" aria-hidden="${selectedId ? "false" : "true"}" tabindex="-1">
        ${selectedId ? renderDrawer() : ""}
      </aside>
    </main>`;

    bind();
  }

  function filterButton(value, label, count) {
    return `<button type="button" data-review-filter="${value}" class="${currentFilter === value ? "on" : ""}" aria-pressed="${currentFilter === value}">${label}<b>${count}</b></button>`;
  }

  function decisionCard(ticket) {
    const copy = decisionCopy(ticket);
    return `<article class="needs-card ${esc(ticket.status)}">
      <div class="needs-card-status" aria-hidden="true"></div>
      <div class="needs-card-body">
        <div class="needs-card-meta">
          <span class="decision-kind ${esc(ticket.status)}">${esc(copy.label)}</span>
          <span>${esc(pretty(ticket.type))}</span>
          <span>${esc(relativeTime(ticket.updatedAt || ticket.createdAt))}</span>
        </div>
        <h3>${esc(ticket.title || ticket.id)}</h3>
        <p>${esc(copy.prompt)}</p>
        <small>${esc(scopeLabel(ticket.scope))}${ticket.worldId ? ` · World ${esc(shortId(ticket.worldId))}` : ""}</small>
      </div>
      <a class="review-open" data-review-open="${esc(ticket.id)}" href="${esc(routeHref(ticketReviewRoute(ticket.id)))}">${assetUid(ticket) ? "Review asset" : "Review context"}</a>
    </article>`;
  }

  function artifactDecisionCard(review) {
    const stale = review.status === "stale";
    const summary = artifactReviewSummary(review);
    const reviewTitle = summary.structure?.label || summary.structure?.name || review.artifactName;
    return `<article class="needs-card ${esc(review.status)} artifact-review-card">
      <div class="needs-card-status" aria-hidden="true"></div>
      <div class="needs-card-body">
        <div class="needs-card-meta"><span class="decision-kind ${stale ? "stale" : "review"}">${stale ? "Out of date" : "Artifact gate"}</span><span>${esc(pretty(review.artifactKind))}</span><span>Revision ${esc(review.revisionNumber)}</span></div>
        <h3>${esc(reviewTitle)}</h3>
        <p>${stale ? "An approved input changed. This revision is retained for history but cannot be promoted; create a successor from current inputs." : esc(summary.request || review.changeNote || "A candidate revision is ready to compare and approve.")}</p>
        <small>${review.mapId ? `World ${esc(shortId(review.mapId))}` : "Project-wide"} · ${esc(relativeTime(review.submittedAt || review.createdAt))}</small>
      </div>
      <a class="review-open" data-artifact-review-open="${esc(review.id)}" href="${esc(routeHref(revisionReviewRoute(review.id)))}">${stale ? "Inspect revision" : "Review revision"}</a>
    </article>`;
  }

  function renderDrawer() {
    if (context?.artifactReview) return renderArtifactDrawer(context.artifactReview);
    const ticket = context?.ticket || tickets.find((item) => item.id === selectedId);
    if (!ticket) return `<div class="drawer-loading">Decision no longer exists.</div>`;
    const copy = decisionCopy(ticket);
    if (loadingContext) return `<div class="drawer-loading">Loading review context…</div>`;

    const attachments = Array.isArray(ticket.spec?.attachments) ? ticket.spec.attachments : [];
    return `<div class="review-drawer-head">
        <div><p class="production-eyebrow">${esc(copy.label)}</p><h2 id="review-dialog-title">${esc(ticket.title || ticket.id)}</h2></div>
        <button type="button" class="drawer-close" data-review-close aria-label="Close review">×</button>
      </div>
      <div class="review-drawer-scroll">
        <section class="review-context-summary">
          <p>${esc(copy.prompt)}</p>
          <dl>
            <div><dt>Work</dt><dd>${esc(pretty(ticket.type))}</dd></div>
            <div><dt>Scope</dt><dd>${esc(scopeLabel(ticket.scope))}</dd></div>
            <div><dt>Status</dt><dd>${esc(pretty(ticket.status))}</dd></div>
            <div><dt>Updated</dt><dd>${esc(absoluteTime(ticket.updatedAt || ticket.createdAt))}</dd></div>
          </dl>
        </section>

        ${renderPreview(ticket, attachments)}
        ${renderArtifacts()}
        ${renderResult(ticket)}
        ${renderEvents(context?.events || [])}

        <section class="review-context-actions">
          <h3>Your decision</h3>
          <label for="review-note">Feedback <span>${ticket.status === "review" ? "— required when requesting changes" : "— tell the next person or agent why"}</span></label>
          <textarea id="review-note" rows="4" placeholder="What is working, what should change, and why?"></textarea>
          <div class="review-decision-buttons">${decisionButtons(ticket)}</div>
          <div class="review-context-links">
            ${ticket.worldId && canNavigateTo("world") ? `<a data-review-world="${esc(ticket.worldId)}" href="${esc(routeHref({ route: "world", worldId: ticket.worldId }))}">Open in World</a>` : ""}
            ${canNavigateTo("ticket") ? `<a data-review-ticket="${esc(ticket.id)}" href="${esc(routeHref({ route: "ticket", ticketId: ticket.id }))}">Open ticket details</a>` : ""}
          </div>
        </section>
      </div>`;
  }

  function renderArtifactDrawer(review) {
    const stale = review.status === "stale";
    const canApprove = review.canApprove !== false;
    const isStructureRevision = review.artifactKind === "building_plan";
    const revisionLabel = `R${String(review.revisionNumber || 0).padStart(2, "0")}`;
    const summary = artifactReviewSummary(review);
    const reviewedStructure = summary.structure;
    const reviewTitle = reviewedStructure?.label || reviewedStructure?.name || review.artifactName;
    const structureId = reviewedStructure?.sourceStructureId || reviewedStructure?.id || "";
    const structureRoute = structureId ? { route: "structure", structureId, structureName: reviewTitle, state: { revision: review.id } } : null;
    const previewRoute = structureId ? { route: "preview", structureId, structureName: reviewTitle, state: { revision: review.id, version: revisionLabel } } : null;
    const validationCount = review.validation && typeof review.validation === "object" ? Object.keys(review.validation).length : 0;
    return `<div class="review-drawer-head">
        <div><p class="production-eyebrow">${stale ? "Out of date" : "Waiting for your decision"} · ${esc(revisionLabel)}</p><h2 id="review-dialog-title">${esc(reviewTitle)}</h2></div>
        <button type="button" class="drawer-close" data-review-close aria-label="Close review">×</button>
      </div>
      <div class="review-drawer-scroll">
        <section class="review-context-summary review-artifact-summary"><p>${stale ? "This immutable revision used an input that has since changed. It remains available as history, but it is no longer eligible for approval." : summary.request ? `<strong>Requested:</strong> ${esc(summary.request)}` : "Review this immutable candidate before it becomes the project baseline."}</p><dl>
          <div><dt>Artifact</dt><dd>${esc(pretty(review.artifactKind))}</dd></div>
          <div><dt>Status</dt><dd>${esc(pretty(review.status))}</dd></div>
          <div><dt>Created by</dt><dd>${esc(review.createdBy)}</dd></div>
          <div><dt>Submitted</dt><dd>${esc(absoluteTime(review.submittedAt || review.createdAt))}</dd></div>
        </dl></section>
        ${summary.facts.length ? `<section class="review-structure-evidence" aria-label="What you are approving"><header><span>WHAT YOU ARE APPROVING</span><b>${esc(revisionLabel)} at a glance</b></header><dl>${summary.facts.map((fact) => `<div><dt>${esc(fact.label)}</dt><dd>${esc(fact.value)}</dd></div>`).join("")}</dl>${previewRoute || structureRoute ? `<nav aria-label="Inspect this version">${previewRoute ? `<a data-review-preview href="${esc(routeHref(previewRoute))}">Open Three.js preview</a>` : ""}${structureRoute ? `<a data-review-structure href="${esc(routeHref(structureRoute))}">Version history and specification</a>` : ""}</nav>` : ""}</section>` : ""}
        <details class="review-context-block review-technical-evidence"><summary><span>Technical evidence</span><small>Candidate JSON${validationCount ? ` · ${validationCount} validation field${validationCount === 1 ? "" : "s"}` : ""}</small></summary><h3>Immutable candidate content</h3><pre class="json">${esc(JSON.stringify(review.body, null, 2))}</pre>${validationCount ? `<h3>Validation evidence</h3><pre class="json">${esc(JSON.stringify(review.validation, null, 2))}</pre>` : ""}</details>
        ${stale ? `<section class="review-context-actions"><h3>No decision available</h3><p>This revision cannot be approved or rejected because its inputs are out of date. Return to Studio or the Board to create a successor from the current approved baseline.</p>
          ${review.mapId && canNavigateTo("world") ? `<div class="review-context-links"><a data-review-world="${esc(review.mapId)}" href="${esc(routeHref({ route: "world", worldId: review.mapId }))}">Open current World</a></div>` : ""}</section>` : `<section class="review-context-actions review-artifact-actions"><h3>Your decision</h3>
          <div class="review-approval-gate"><span>APPROVAL GATE</span><b>Make ${esc(revisionLabel)} the Structure Atlas baseline</b><p>This promotes this exact immutable version. It does not export, import, or change anything in Unreal.</p>
          ${canApprove ? `<button type="button" class="approve review-primary-approval" data-artifact-review-action="approve">Approve ${esc(revisionLabel)} as baseline</button>` : `<div class="review-approval-separation" role="status"><b>Another reviewer is required</b><p>This version is attributed to your identity. Ask another project owner or reviewer to approve it; you may still request a revision or reject the branch.</p></div>`}</div>
          <details class="review-revision-request"><summary>Request another revision</summary><div><label for="review-change-note">What should the next version change? <span>Required</span></label><textarea id="review-change-note" rows="4" placeholder="Keep the tunnel. Add three 3.5 m road lanes with…"></textarea>
          ${isStructureRevision ? `<div class="review-revision-choices">
            <article><span>KEEP THIS WORK</span><b>Continue from ${esc(revisionLabel)}</b><p>Preserve this version and apply your next change on top of it.</p><button type="button" data-artifact-review-action="change_requested" data-branch-strategy="continue">Continue ${esc(revisionLabel)}</button></article>
            <article><span>START FROM ITS PARENT</span><b>Try the last change again</b><p>Use your notes to create a sibling from the version before ${esc(revisionLabel)}.</p><button type="button" data-artifact-review-action="change_requested" data-branch-strategy="retry_parent">Retry from parent</button></article>
          </div>` : `<div class="review-decision-buttons"><button type="button" data-artifact-review-action="change_requested">Request changes</button></div>`}</div></details>
          <details class="review-danger-zone"><summary>Reject this branch</summary><div><p>Reject ${esc(revisionLabel)} completely and retain it only as immutable history. Future work cannot use it as a baseline.</p><label for="review-reject-note">Why should this branch stop? <span>Required</span></label><textarea id="review-reject-note" rows="3" placeholder="This version should not be reused because…"></textarea><button type="button" class="reject" data-artifact-review-action="reject" data-branch-strategy="reject">Reject ${esc(revisionLabel)}</button></div></details>
          ${review.mapId && canNavigateTo("world") ? `<div class="review-context-links"><a data-review-world="${esc(review.mapId)}" href="${esc(routeHref({ route: "world", worldId: review.mapId }))}">Open in World</a></div>` : ""}
        </section>`}
      </div>`;
  }

  function renderPreview(ticket, attachments) {
    const imageAttachments = attachments.filter(isImageKey);
    if (!imageAttachments.length) return "";
    return `<section class="review-context-block"><h3>Submitted references</h3><div class="review-preview-grid">
      ${imageAttachments.map((key) => imagePreview({ uri: key, label: `Reference for ${ticket.title || "review"}`, alt: `Reference for ${ticket.title || "review"}`, esc, fileUrl })).join("")}
    </div></section>`;
  }

  function renderArtifacts() {
    if (!artifacts.length) return "";
    return `<section class="review-context-block"><h3>Output artifacts</h3><div class="review-artifacts">
      ${artifacts.map((artifact) => `<div><span>${esc(pretty(artifact.kind || "artifact"))}</span><small>${artifactLink({ uri: artifact.uri, label: fileName(artifact.uri), esc, fileUrl })}</small></div>`).join("")}
    </div></section>`;
  }

  function renderResult(ticket) {
    if (!ticket.result) return "";
    return `<details class="review-context-block"><summary>Result details</summary><pre class="json">${esc(JSON.stringify(ticket.result, null, 2))}</pre></details>`;
  }

  function renderEvents(events) {
    if (!events.length) return "";
    return `<details class="review-context-block"><summary>Decision history</summary><ol class="review-history">
      ${events.map((event) => `<li><span>${esc(absoluteTime(event.at))}</span><b>${esc(pretty(event.toStatus || "event"))}</b>${event.note ? `<p>${esc(event.note)}</p>` : ""}</li>`).join("")}
    </ol></details>`;
  }

  function decisionButtons(ticket) {
    if (ticket.status === "review") {
      return `<button type="button" class="approve" data-review-action="approved">Approve</button><button type="button" class="reject" data-review-action="rejected">Request changes</button>`;
    }
    if (ticket.status === "escalated") {
      return `<button type="button" class="approve" data-review-action="ready">Retry work</button><button type="button" class="reject" data-review-action="cancelled">Cancel work</button>`;
    }
    return `<button type="button" class="approve" data-review-action="ready">Rebuild with current direction</button><button type="button" data-review-action="cancelled">Archive this work</button>`;
  }

  function emptyState() {
    return `<div class="needs-empty"><span>✓</span><h3>No decisions waiting</h3><p>Agents can keep working. New gates and exceptions will appear here.</p></div>`;
  }

  function bind() {
    bindArtifactLightboxes(root);
    root.querySelector("[data-review-refresh]")?.addEventListener("click", render);
    root.querySelectorAll("[data-review-filter]").forEach((button) => {
      button.addEventListener("click", () => {
        currentFilter = button.dataset.reviewFilter;
        localStorage.setItem("wl_review_filter", currentFilter);
        draw();
        publishRoute(selectedRoute());
      });
    });
    root.querySelectorAll("[data-review-open]").forEach((link) => {
      link.addEventListener("click", (event) => {
        returnFocus = link;
        activateRouteLink(event, ticketReviewRoute(link.dataset.reviewOpen), () => open(link.dataset.reviewOpen));
      });
    });
    root.querySelectorAll("[data-artifact-review-open]").forEach((link) => {
      link.addEventListener("click", (event) => {
        returnFocus = link;
        activateRouteLink(event, revisionReviewRoute(link.dataset.artifactReviewOpen), () => openArtifact(link.dataset.artifactReviewOpen));
      });
    });
    root.querySelectorAll("[data-review-close]").forEach((element) => {
      element.addEventListener("click", () => close());
    });
    root.querySelector("[data-review-ticket]")?.addEventListener("click", (event) => {
      const ticketId = event.currentTarget.dataset.reviewTicket;
      activateRouteLink(event, { route: "ticket", ticketId }, () => openTicket?.(ticketId));
    });
    root.querySelectorAll("[data-review-world]").forEach((link) => link.addEventListener("click", (event) => {
      const worldId = event.currentTarget.dataset.reviewWorld;
      activateRouteLink(event, { route: "world", worldId }, () => openWorld?.(worldId));
    }));
    root.querySelector("[data-review-preview]")?.addEventListener("click", (event) => {
      const review = context?.artifactReview;
      const structure = artifactReviewSummary(review).structure;
      const structureId = structure?.sourceStructureId || structure?.id;
      if (structureId) activateRouteLink(event, {
        route: "preview",
        structureId,
        structureName: structure.label || structure.name || review.artifactName,
        state: { revision: review.id, version: `R${String(review.revisionNumber || 0).padStart(2, "0")}` },
      });
    });
    root.querySelector("[data-review-structure]")?.addEventListener("click", (event) => {
      const review = context?.artifactReview;
      const structure = artifactReviewSummary(review).structure;
      const structureId = structure?.sourceStructureId || structure?.id;
      if (structureId) activateRouteLink(event, {
        route: "structureChange",
        structureId,
        structureName: structure.label || structure.name || review.artifactName,
        state: { revision: review.id },
      });
    });
    root.querySelectorAll("[data-review-action]").forEach((button) => {
      button.addEventListener("click", () => decide(button.dataset.reviewAction));
    });
    root.querySelectorAll("[data-artifact-review-action]").forEach((button) => {
      button.addEventListener("click", () => decideArtifact(button.dataset.artifactReviewAction, button.dataset.branchStrategy || ""));
    });
  }

  async function open(ticketId, { publish = true } = {}) {
    selectedId = ticketId;
    if (publish) publishRoute(ticketReviewRoute(ticketId));
    loadingContext = true;
    context = null;
    artifacts = [];
    draw();
    try {
      const [ticketResponse, artifactResponse] = await Promise.all([
        api(`/tickets/${ticketId}`),
        api(`/artifacts?projectId=${encodeURIComponent(document.body.dataset.projectId || "")}&ticketId=${ticketId}`).catch(() => ({ artifacts: [] })),
      ]);
      context = ticketResponse;
      artifacts = Array.isArray(artifactResponse.artifacts) ? artifactResponse.artifacts : [];
      const uid = assetUid(ticketResponse.ticket);
      if (uid && typeof openAssetReview === "function") {
        const review = { assetUid: uid, ticket: ticketResponse.ticket, events: ticketResponse.events || [], artifacts };
        selectedId = "";
        context = null;
        artifacts = [];
        await openAssetReview(review);
        return;
      }
    } catch (error) {
      toast?.(readableError(error), true);
      selectedId = "";
    } finally {
      loadingContext = false;
      draw();
      root.querySelector(".review-drawer")?.focus?.();
    }
  }

  function openArtifact(revisionId, { publish = true } = {}) {
    const review = artifactReviews.find((item) => item.id === revisionId);
    if (!review) return;
    selectedId = revisionId;
    context = { artifactReview: review };
    artifacts = [];
    draw();
    if (publish) publishRoute(revisionReviewRoute(revisionId));
    const drawer = root.querySelector(".review-drawer");
    (root.querySelector("[data-artifact-review-action=\"approve\"]")
      || drawer?.querySelector("button:not([disabled]), a[href], details > summary, textarea")
      || drawer)?.focus?.();
  }

  function close({ publish = true } = {}) {
    selectedId = "";
    context = null;
    artifacts = [];
    draw();
    if (publish) publishRoute({ route: "reviews", state: reviewFilterState() });
    returnFocus?.focus?.();
    returnFocus = null;
  }

  async function applyRouteState({ filter: nextFilter, ticketId = "", revisionId = "" } = {}) {
    if (["all", "review", "escalated", "stale"].includes(nextFilter)) {
      currentFilter = nextFilter;
      localStorage.setItem("wl_review_filter", currentFilter);
    }
    await render();
    if (ticketId) return open(ticketId, { publish: false });
    if (revisionId) openArtifact(revisionId, { publish: false });
  }

  function getRouteState() {
    const state = reviewFilterState();
    if (context?.artifactReview) return { ...state, revisionId: context.artifactReview.id };
    if (selectedId) return { ...state, ticketId: selectedId };
    return state;
  }

  function reviewFilterState() {
    return currentFilter === "all" ? {} : { filter: currentFilter };
  }

  function ticketReviewRoute(ticketId) {
    const ticket = tickets.find((item) => item.id === ticketId) || (context?.ticket?.id === ticketId ? context.ticket : null);
    return { route: "ticketReview", ticketId, ticketName: ticket?.title || ticket?.type || "review", state: reviewFilterState() };
  }

  function revisionReviewRoute(revisionId) {
    const review = artifactReviews.find((item) => item.id === revisionId);
    const reviewedStructure = Array.isArray(review?.body?.radialStructures) ? review.body.radialStructures[0] : null;
    const revisionLabel = review ? `R${String(review.revisionNumber || 0).padStart(2, "0")}` : "revision";
    const revisionName = reviewedStructure?.label || reviewedStructure?.name || review?.artifactName || review?.kind || "revision";
    return { route: "revisionReview", revisionId, revisionName: `${revisionName} ${revisionLabel}`, state: reviewFilterState() };
  }

  function selectedRoute() {
    const state = getRouteState();
    if (state.ticketId) return ticketReviewRoute(state.ticketId);
    if (state.revisionId) return revisionReviewRoute(state.revisionId);
    return { route: "reviews", state: reviewFilterState() };
  }

  function routeHref(descriptor) {
    return hrefFor?.(descriptor) || "#";
  }

  function canNavigateTo(route) {
    return route === "world" ? Boolean(openWorld || hrefFor || onNavigate) : Boolean(openTicket || hrefFor || onNavigate);
  }

  function publishRoute(descriptor) {
    onRouteStateChange?.(descriptor);
  }

  function activateRouteLink(event, descriptor, fallback) {
    if (!isUnmodifiedPrimaryLinkClick(event)) return;
    event.preventDefault();
    if (onNavigate) onNavigate(descriptor);
    else fallback?.();
  }

  async function decide(to) {
    const ticket = context?.ticket || tickets.find((item) => item.id === selectedId);
    if (!ticket) return;
    const note = root.querySelector("#review-note")?.value.trim() || "";
    if (to === "rejected" && !note) {
      toast?.("Describe what should change before requesting a revision", true);
      root.querySelector("#review-note")?.focus();
      return;
    }
    const button = root.querySelector(`[data-review-action="${to}"]`);
    button?.classList.add("working");
    button && (button.disabled = true);
    try {
      await api(`/tickets/${ticket.id}/transition`, {
        method: "POST",
        body: JSON.stringify({ to, actor: "human", note: note || defaultNote(to) }),
      });
      toast?.(`${ticket.title || "Work"} → ${decisionLabel(to)}`);
      selectedId = "";
      context = null;
      artifacts = [];
      await render();
      publishRoute({ route: "reviews", state: reviewFilterState() });
    } catch (error) {
      toast?.(readableError(error), true);
      button?.classList.remove("working");
      button && (button.disabled = false);
    }
  }

  async function decideArtifact(decision, branchStrategy = "") {
    const review = context?.artifactReview;
    if (!review) return;
    const noteSelector = decision === "reject" ? "#review-reject-note" : decision === "change_requested" ? "#review-change-note" : "#review-approval-note";
    const note = root.querySelector(noteSelector)?.value.trim() || "";
    if (["change_requested", "reject"].includes(decision) && !note) {
      toast?.(decision === "reject" ? "Explain why this branch must stop" : "Describe what the next revision should change", true);
      root.querySelector(noteSelector)?.focus();
      return;
    }
    const button = branchStrategy
      ? root.querySelector(`[data-artifact-review-action="${decision}"][data-branch-strategy="${branchStrategy}"]`)
      : root.querySelector(`[data-artifact-review-action="${decision}"]`);
    button && (button.disabled = true);
    try {
      const projectId = document.body.dataset.projectId || "";
      await api(`/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(review.artifactId)}/revisions/${encodeURIComponent(review.id)}/review`, {
        method: "POST",
        body: JSON.stringify({ decision, note }),
      });
      toast?.(`${review.artifactName} → ${decision === "approve" ? "approved baseline" : decision === "reject" ? "rejected and retained as history" : "revision requested"}`);
      const sourceStructure = Array.isArray(review.body?.radialStructures) ? review.body.radialStructures[0] : null;
      const sourceStructureId = sourceStructure?.sourceStructureId || sourceStructure?.id || "";
      const openStructureBranch = decision === "change_requested" && review.artifactKind === "building_plan" && sourceStructureId && typeof onNavigate === "function";
      const openRejectedStructure = decision === "reject" && review.artifactKind === "building_plan" && sourceStructureId && typeof onNavigate === "function";
      selectedId = "";
      context = null;
      await render();
      window.dispatchEvent(new CustomEvent("worldloom:artifact-reviewed"));
      if (openStructureBranch) onNavigate({
        route: "structure",
        structureId: sourceStructureId,
        structureName: sourceStructure?.label || sourceStructure?.name || "Structure",
        state: branchStrategy === "retry_parent" ? { retry: review.id } : { successor: review.id },
      });
      else if (openRejectedStructure) onNavigate({
        route: "structure",
        structureId: sourceStructureId,
        structureName: sourceStructure?.label || sourceStructure?.name || "Structure",
        state: { revision: review.id },
      });
      else publishRoute({ route: "reviews", state: reviewFilterState() });
    } catch (error) {
      toast?.(readableError(error), true);
      button && (button.disabled = false);
    }
  }

  function countStatus(status) {
    return tickets.filter((ticket) => ticket.status === status).length + artifactReviews.filter((review) => review.status === status).length;
  }

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && selectedId) close();
    if (event.key === "Tab" && selectedId) {
      const drawer = root.querySelector(".review-drawer.open");
      const focusable = Array.from(drawer?.querySelectorAll('a[href], button:not([disabled]), textarea, select, details > summary, [tabindex]:not([tabindex="-1"])') || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  return { render, open, openArtifact, close, refreshCount, applyRouteState, getRouteState, getRouteDescriptor: selectedRoute };
}

function isUnmodifiedPrimaryLinkClick(event) {
  return event.button === 0
    && !event.defaultPrevented
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey;
}

function decisionCopy(ticket) {
  if (ticket.status === "review") return { label: "Approval", prompt: "Review the submitted result and decide whether it becomes approved work." };
  if (ticket.status === "escalated") return { label: "Exception", prompt: "Automation could not finish this work. Decide whether to retry it or stop it." };
  return { label: "Direction changed", prompt: "This work was made against an older direction. Decide whether it should be rebuilt or archived." };
}

function decisionSort(a, b) {
  const rank = { review: 0, escalated: 1, stale: 2 };
  const statusRank = (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
  if (statusRank) return statusRank;
  return String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || ""));
}

function scopeLabel(scope) {
  if (!scope || scope.kind === "global") return "Whole project";
  if (scope.kind === "cell") return `Section ${scope.cellId || ""}`.trim();
  if (scope.kind === "asset") return `Asset ${shortId(scope.assetUid)}`;
  if (scope.kind === "placement") return `Placement ${shortId(scope.placementUid)}`;
  return "Project work";
}

function assetUid(ticket) {
  if (ticket?.scope?.kind === "asset" && ticket.scope.assetUid) return ticket.scope.assetUid;
  if (ticket?.result?.assetUid) return ticket.result.assetUid;
  return "";
}

function defaultNote(to) {
  if (to === "approved") return "Approved in Needs you";
  if (to === "ready") return "Retry requested in Needs you";
  if (to === "cancelled") return "Archived in Needs you";
  return "Decision made in Needs you";
}

function decisionLabel(to) {
  return ({ approved: "approved", rejected: "changes requested", ready: "ready to retry", cancelled: "archived" })[to] || pretty(to);
}

function pretty(value) {
  const text = String(value || "").replaceAll("_", " ");
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : "";
}

function shortId(value) {
  return String(value || "").slice(0, 8);
}

function relativeTime(value) {
  if (!value) return "Not started";
  const delta = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(delta)) return "Recently";
  const minutes = Math.max(0, Math.floor(delta / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function absoluteTime(value) {
  return value ? new Date(value).toLocaleString() : "Unknown";
}

function isImageKey(value) {
  return /\.(png|jpe?g|webp|gif|avif)(\?.*)?$/i.test(String(value || ""));
}

function fileName(value) {
  return String(value || "artifact").split("/").pop() || "artifact";
}

function readableError(error) {
  return error instanceof Error ? error.message : String(error);
}
